/**
 * The front door: what needs the analyst today.
 *
 * It used to open with the platform's own shape — Organizations 1, Tenants 1,
 * both "-100%", the active tenant's raw id — above a live feed of every
 * cross-chain transaction, with a "Connect wallet" button. None of that is an
 * investigator's work: their day starts with what fired overnight, which cases
 * moved, and what is still being watched. Those three lanes are the page now
 * (`./queue`), and the live feed stays below them as context rather than as the
 * answer. The tenancy counts were not moved anywhere: Settings › Organizations &
 * tenants already lists orgs and tenants, with the names and the actions.
 *
 * A fresh tenant still gets the onboarding guide instead — the decision is held
 * until the probes resolve so a set-up tenant never flashes it.
 *
 * Charting stays out of this chunk on purpose: the KPI tiles were rendered with
 * `ui-core` primitives rather than `@authe/analytics-widgets` (tremor + recharts)
 * so the eager landing bundle stays small, and the queue keeps that property.
 */
import * as React from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { useSession } from "@authe/ui-core/session";
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton } from "@authe/ui-core";
import { Compass } from "lucide-react";
import { TransactionTable, useTransactionsQuery } from "@authe/tx-table";
import { PageHeader } from "@/components/PageHeader";
import { OnchainActivityPanel } from "./onchain";
import { GettingStarted, useOnboardingProgress } from "./onboarding";
import { ActiveCasesLane, FiredAlertsLane, LiveWatchesLane } from "./queue";
import { ROUTES } from "@/config";
import { useSubjectOpener } from "@/features/inspector";

/** First name only: the greeting is an address, not an identity check. */
function firstName(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

export function OverviewPage(): React.ReactElement {
  const navigate = useNavigate();
  const opener = useSubjectOpener();
  const { session } = useSession();

  // Auth0-style onboarding: a fresh tenant lands on the "Getting started" guide
  // (progress inferred from real data); an established/dismissed tenant gets the
  // queue. `?guide=1` forces the guide either way.
  //
  // The decision needs the probes, so until they answer this page renders
  // NEITHER. It used to render the queue while they were in flight, which meant
  // a tenant mid-setup watched their front door load a queue and then replace
  // it with a checklist — the one screen in the product where a content swap is
  // least affordable, and it happened on every single load. Holding is honest:
  // we do not yet know which page this is. It costs nothing in the common case,
  // because a dismissed guide skips the probes entirely.
  const search = useSearch({ strict: false }) as { guide?: boolean };
  // Probe when the guide is being forced, for the same reason
  // `GettingStartedPage` does — `?guide=1` exists to override a dismissal.
  const onboarding = useOnboardingProgress({ probe: Boolean(search.guide) });
  const forced = Boolean(search.guide);
  const undecided = !forced && !onboarding.dismissed && onboarding.isLoading;
  // `unknown` falls towards the QUEUE, deliberately. The two mistakes are not
  // symmetric: wrongly showing a setup checklist to an established tenant hides
  // the work they came here for, while wrongly showing the queue to a new
  // tenant costs one click on "Getting started" — which is on this header, in
  // the nav, and in the palette. One failed probe used to be enough to replace
  // a working analyst's front door with "Invite your team".
  const showGuide =
    forced ||
    (!onboarding.dismissed &&
      !onboarding.isLoading &&
      !onboarding.unknown &&
      !onboarding.allComplete);

  const recent = useTransactionsQuery({ pageSize: 10 });
  const greeting = firstName(session?.user.name);

  if (undecided) {
    // No heading and no lane skeletons: either would claim which of the two
    // pages this is, and that is precisely what is still being decided. The
    // real page arrives with its own title one probe later.
    return (
      <div className="space-y-4" aria-busy>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (showGuide) {
    return <GettingStarted progress={onboarding} onDismiss={onboarding.dismiss} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={greeting ? `Your queue, ${greeting}` : "Your queue"}
        description="What fired, what moved, and what is still being watched."
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate({ to: ROUTES.gettingStarted })}
            >
              <Compass className="h-4 w-4" />
              Getting started
            </Button>
            <Button size="sm" onClick={() => navigate({ to: ROUTES.investigations })}>
              Start a case
            </Button>
          </div>
        }
      />

      {/* One column, in priority order: alerts first — the only lane where
          something has already happened that nobody has looked at — then the
          work in progress, then what is running on your behalf. Side-by-side
          cards would read as a dashboard; a queue is a list you work down. */}
      <div className="grid gap-4">
        <FiredAlertsLane />
        <ActiveCasesLane />
        <LiveWatchesLane />
      </div>

      <OnchainActivityPanel />

      <Card>
        <CardHeader>
          <CardTitle>Recent transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <TransactionTable
            rows={recent.rows}
            isLoading={recent.isLoading}
            isFetching={recent.isFetching}
            error={recent.error}
            onRetry={recent.refetch}
            onRowClick={opener.openTransaction}
          />
        </CardContent>
      </Card>
    </div>
  );
}
